import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PublicHeader } from "@/components/PublicHeader";
import { ScreenerTeaser } from "@/components/ScreenerTeaser";
import { PRODUCTS, PRODUCT_SLUGS, PRODUCT_UI } from "@/data/products";
import {
  Activity, ArrowLeft, ArrowRight, Ban, Bell, CalendarClock, Calculator, Coins, Droplet,
  FileText, Gavel, Globe2, Layers, LineChart, Moon, PieChart, Repeat, Scale, Search,
  ShieldCheck, TrendingUp, Unlock, Vault,
} from "lucide-react";

const ICONS = {
  Activity, Ban, Bell, CalendarClock, Calculator, Coins, Droplet, FileText, Gavel, Globe2,
  Layers, LineChart, Moon, PieChart, Repeat, Scale, Search, ShieldCheck, Unlock, Vault,
};

const NAV_LABEL = {
  en: { trade: "Trade", markets: "Markets", invest: "Invest", save: "Save", halal: "Halal",
        wallet: "Wallet" },
  ar: { trade: "التداول", markets: "الأسواق", invest: "الاستثمار", save: "التوفير",
        halal: "الحلال", wallet: "المحفظة" },
};

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useApp();
  const locale = lang === "ar" ? "ar" : "en";
  const ui = (k) => PRODUCT_UI[locale][k];
  if (!PRODUCT_SLUGS.includes(slug)) return <Navigate to="/" replace />;
  const p = PRODUCTS[locale][slug];
  const secondary = slug === "halal"
    ? { to: "/top-halal-stocks", label: ui("pr_cta_explore") }
    : slug === "markets"
      ? { to: "/exchanges", label: ui("pr_cta_exchanges") }
      : slug === "invest"
        ? { to: "/portfolios", label: ui("pr_cta_portfolios") }
        : { to: "/fees", label: ui("pr_cta_pricing") };

  return (
    <div className="min-h-screen bg-background" data-testid={`product-${slug}-page`}>
      <PublicHeader />
      {/* hero */}
      <div className="navy-strip text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-sheen opacity-60" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-16">
          <div className="mb-8 flex items-center justify-between gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}
              className="text-white/70 hover:text-white hover:bg-white/10" data-testid="product-back">
              <ArrowLeft className="h-4 w-4 me-1.5" />{ui("pr_back")}
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">
            <TrendingUp className="h-3.5 w-3.5" />{p.eyebrow}
          </div>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            data-testid="product-title">
            {p.h1a}<br />
            <span className="text-white/70">{p.h1b}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed" data-testid="product-sub">
            {p.sub}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button className="rounded-full px-7 h-11" onClick={() => navigate("/login")}
              data-testid="product-cta-open">
              {ui("pr_cta_open")} <ArrowRight className="h-4 w-4 ms-1.5" />
            </Button>
            <Button variant="outline"
              className="rounded-full px-7 h-11 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
              onClick={() => navigate(secondary.to)} data-testid="product-cta-screener">
              {secondary.label}
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="product-stats">
            {p.stats.map(([k, v]) => (
              <div key={k} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">{k}</div>
                <div className="mt-1 numeric text-lg font-bold">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 sm:py-20 space-y-16 sm:space-y-24">
        {/* how it works */}
        <section data-testid="product-steps">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{ui("pr_how")}</h2>
          <div className="mt-8 space-y-3">
            {p.steps.map(([title, body], i) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-5 sm:flex sm:gap-8"
                data-testid={`product-step-${i + 1}`}>
                <div className="sm:w-64 shrink-0 flex items-start gap-3">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#1A1330] text-[color:var(--tharwy-lime)] text-xs font-bold">
                    {i + 1}
                  </span>
                  <div className="text-sm font-semibold leading-snug">{title}</div>
                </div>
                <p className="mt-2 sm:mt-0 text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* features */}
        <section data-testid="product-features">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{ui("pr_what")}</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {p.features.map(([icon, title, body]) => {
              const Icon = ICONS[icon] || ShieldCheck;
              return (
                <Card key={title} className="p-6 shadow-premium">
                  <div className="h-10 w-10 rounded-2xl bg-secondary grid place-items-center text-[#3E2E65]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-base md:text-lg font-semibold leading-snug">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* live screener slice — real data before anyone signs up */}
        {(slug === "trade" || slug === "halal") && <ScreenerTeaser />}

        {/* faq */}
        <section data-testid="product-faq">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{ui("pr_faq")}</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {p.faq.map(([q, a]) => (
              <div key={q} className="rounded-2xl border border-border bg-card p-5">
                <div className="text-sm font-semibold leading-snug">{q}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground leading-relaxed" data-testid="product-note">{p.note}</p>
        </section>

        {/* related */}
        <section data-testid="product-related">
          <h2 className="text-base md:text-lg font-semibold">{ui("pr_related")}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {PRODUCT_SLUGS.filter((s) => s !== slug).map((s) => (
              <button key={s} onClick={() => navigate(`/product/${s}`)}
                className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`product-related-${s}`}>
                {NAV_LABEL[locale][s]}
              </button>
            ))}
            <button onClick={() => navigate("/fees")}
              className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
              data-testid="product-related-pricing">
              {locale === "ar" ? "الأسعار" : "Pricing"}
            </button>
          </div>
        </section>

        {/* closing cta */}
        <section className="rounded-3xl navy-strip text-white p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 hero-sheen opacity-50" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{ui("pr_cta_title")}</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto">{ui("pr_cta_sub")}</p>
            <Button className="mt-7 rounded-full px-8 h-11" onClick={() => navigate("/login")}
              data-testid="product-cta-bottom">
              {ui("pr_cta_open")} <ArrowRight className="h-4 w-4 ms-1.5" />
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
